import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, gql } from '@apollo/client';
import { TextInput, Modal } from '@carbon/react';
import MultiStudentSelector from '../../../Components/multiStudentSelector';

const CREATE_GROUP = gql`
  mutation CreateGroup($name: String!, $studentIds: [String!]!) {
    createGroup(name: $name, studentIds: $studentIds) {
      id
      name
    }
  }
`;

const GET_ALL_GROUPS = gql`
  query GetAllGroups {
    allGroups {
      id
      name
    }
  }
`;

export default function CreateGroup() {
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [continueEnabled, setContinueEnabled] = useState(false);

  const [createGroup, { loading }] = useMutation(CREATE_GROUP, {
    refetchQueries: [{ query: GET_ALL_GROUPS }],
    awaitRefetchQueries: true,
    errorPolicy: 'all',
  });

  useEffect(() => {
    let err = '';
    if (!groupName.trim()) err = 'Gruppenname ist erforderlich.';
    setErrorMessage(err);
    setContinueEnabled(!err);
  }, [groupName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!continueEnabled) return;
    try {
      const res = await createGroup({ variables: { name: groupName.trim(), studentIds: selectedStudents } });

      // If GraphQL errors are returned in the response (errorPolicy: 'all'), show them
      if (res?.errors && res.errors.length > 0) {
        setErrorMessage(res.errors.map((er) => er.message).join('; '));
        return;
      }

      // If no data or unexpected shape, treat as error
      if (!res?.data || !res.data.createGroup) {
        setErrorMessage('Fehler beim Erstellen der Gruppe. Keine Antwort vom Server.');
        return;
      }

      navigate('/admin/groups');
    } catch (err) {
      // Apollo errors can contain graphQLErrors / networkError - prefer detailed messages
      if (err?.graphQLErrors && err.graphQLErrors.length) {
        setErrorMessage(err.graphQLErrors.map((e) => e.message).join('; '));
      } else if (err?.networkError) {
        setErrorMessage('Netzwerkfehler: ' + (err.networkError.message || err.message));
      } else {
        setErrorMessage(err.message || 'Unbekannter Fehler');
      }
      console.error('Create group error', err);
    }
  };

  return (
    <div className="py-4">
      <div className="max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900">Gruppe erstellen</h1>
        <p className="mt-2 text-sm text-gray-600">Erstelle eine neue Studentengruppe.</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <TextInput
            id="groupName"
            labelText="Gruppenname"
            placeholder="z.B. BKUS"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            required
          />

          <MultiStudentSelector selectedStudents={selectedStudents} onChange={setSelectedStudents} />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
            <p className="text-sm text-red-600 h-5">{errorMessage}</p>
            <button
              type="submit"
              className={`mainBtn w-full sm:w-auto text-white px-6 py-2 transition-opacity duration-200 ${continueEnabled ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'}`}
              disabled={!continueEnabled || loading}
            >
              Gruppe erstellen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
